/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { TicketContainer } from '../tickets/TicketContainer';
import { TICKET_CHANGE_EVENTS } from '../tickets/TicketContainer';
import { TICKET_DATE_TEXT_SETTINGS } from './TICKET_TEXT_SETTINGS';
import { Ticket, Side } from '@touchplan/common';
import { TicketUtils } from '@appCanvasCommon/utils/TicketUtils';
import { TicketTextCanvas } from './TicketTextCanvas';
import { dateCache } from '@touchplan/common';
import { CanvasAngularLand } from 'src/app/app-data/interfaces/canvas-angular-land';
import { FancyTextStyle } from '@appCanvasCommon/interfaces/fancy-text-style.interface';
import { TicketType } from '@touchplan/common';
import { EventMode } from '@appCanvasCommon/events/event-mode.enum';

export class TicketDateTextCanvas extends TicketTextCanvas {
	private _liveStart: string;
	private _liveFinish: string;
	private _lastLiveStart: string;
	private _lastLiveFinish: string;
	private _unsubUpdate: any;

	constructor(
		canvasAngularLand: CanvasAngularLand,
		ticketContainer: TicketContainer,
		text: string = '',
		style: FancyTextStyle = new FancyTextStyle({ wordWrap: false, fill: '#ffffff' }),
		canvas?: HTMLCanvasElement
	) {
		super(text, style, canvasAngularLand, ticketContainer, canvas);

		ticketContainer.on(TICKET_CHANGE_EVENTS.CHANGED, this.onChanged);
		this._unsubUpdate = ticketContainer.camera.changed$.subscribe(this.update);

		this.onChanged(ticketContainer);
		this.eventMode = EventMode.None;
	}

	public update = () => {
		if (!this.ticket.visible || !this.ticket.renderable) {
			return;
		}
		this.onChanged(this.ticket);
	};

	public override destroy(options?): void {
		this.ticket.off(TICKET_CHANGE_EVENTS.CHANGED, this.onChanged);
		this._unsubUpdate.unsubscribe();
		super.destroy(options);
	}

	private onChanged = (ticketContainer: TicketContainer) => {
		const data = ticketContainer.getData();
		this._liveStart = data.view.liveStart;
		this._liveFinish = data.view.liveFinish;
		this.renderable =
			(data.view.liveStart || data.view.liveFinish) && ticketContainer.renderable && this.visible;

		if (this.renderable) {
			this.tint = this.ticket.liveRoleTextColor;

			this.applyOffsetAndDrawArea(ticketContainer, TICKET_DATE_TEXT_SETTINGS);
			const type = TicketUtils.getTicketType(data);
			if (type === TicketType.Milestone || type === TicketType.Constraint) {
				this.style.align = 'center';
				this.align.x = true;
			}
			this.x = this.maskArea.x;
			this.y = this.maskArea.y + this._getLocationHeightOffset(data);

			this.style.fontSize = data.view.side === Side.PULL ? 22 : 16;

			if (this._lastLiveFinish !== data.view.liveFinish || this._lastLiveStart !== data.view.liveStart) {
				const formattedDate: string = this._format(
					data.view.liveStart,
					data.view.liveFinish,
					data.view.isActualized,
					data.view.type === TicketType.Constraint
				);
				this.setText(formattedDate);
			}

			this.positionCenter(data);

			this._lastLiveStart = data.view.liveStart;
			this._lastLiveFinish = data.view.liveFinish;

			// MAKE LESS VISIBLE
			if (data.view.isActualized) {
				this.alpha = 0.5;
			} else {
				this.alpha = 1.0;
			}
		}
	};

	private _format(
		startDate: string,
		finishDate: string,
		isComplete: boolean = false,
		forceNoRange: boolean = false
	) {
		const start = startDate;
		const end = finishDate;
		const dateToUse = isComplete ? end : start;
		return !forceNoRange && start !== end && start
			? dateCache.getFormat(start, 'MMM DD') + ' - ' + dateCache.getFormat(end, 'MMM DD')
			: dateCache.getFormat(dateToUse, 'MMM DD');
	}

	private _getLocationHeightOffset(ticket: Ticket): number {
		let _offset = 0;
		const type = TicketUtils.getTicketType(ticket);
		if (ticket.view.location && type === TicketType.Task) {
			_offset += ticket.view.side === Side.PULL ? 40 : 25;
		}

		return _offset;
	}
}
